import { Box, Button, Chip, FormControl, FormHelperText, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Textarea } from './mui-styled-textarea';

/**
 * Live-validating editor for the json/jsonb columns: data_sources.config,
 * data_sources.options and data_sources_layers.colours.
 *
 * The value stays a string the whole time it's being edited. Holding a parsed
 * object would make the field unusable, since almost every intermediate
 * keystroke is invalid JSON — parsing happens only to produce the error message
 * here, and again in fromFormValues on submit.
 *
 * Built on the app's Textarea rather than a bare MUI TextField so it sits with
 * the other textarea fields; NoteField isn't reused because importing it from
 * form-components would make the import circular.
 */

export type JsonValidation = {
  valid: boolean;
  error: string | null;
  line: number | null;
  column: number | null;
  parsed: any;
};

const EMPTY: JsonValidation = { valid: true, error: null, line: null, column: null, parsed: null };

/**
 * V8 reports "at position N" (and newer versions a line/column of their own).
 * Deriving it from the offset keeps the message identical across runtimes.
 */
const positionFromError = (text: string, message: string) => {
  const match = /position (\d+)/.exec(message);
  if (!match) return { line: null, column: null };

  const offset = Math.min(Number(match[1]), text.length);
  const lines = text.slice(0, offset).split('\n');
  return { line: lines.length, column: lines[lines.length - 1].length + 1 };
};

export const validateJson = (text: string, { required = false } = {}): JsonValidation => {
  const trimmed = (text || '').trim();

  if (trimmed === '') {
    return required ? { ...EMPTY, valid: false, error: 'This field is required.' } : EMPTY;
  }

  try {
    return { valid: true, error: null, line: null, column: null, parsed: JSON.parse(trimmed) };
  } catch (e: any) {
    const message = String(e?.message || 'Invalid JSON.');
    const { line, column } = positionFromError(trimmed, message);
    const clean = message.replace(/\s*(in JSON )?at position \d+.*$/i, '');
    return { valid: false, error: clean || 'Invalid JSON.', line, column, parsed: null };
  }
};

const describe = (parsed: any): string | null => {
  if (parsed === null) return null;
  if (Array.isArray(parsed)) return `array · ${parsed.length} item${parsed.length === 1 ? '' : 's'}`;
  if (typeof parsed === 'object') {
    const keys = Object.keys(parsed).length;
    return `object · ${keys} key${keys === 1 ? '' : 's'}`;
  }
  return typeof parsed;
};

interface JsonFieldProps {
  value: any;                        // string while editing, object when loaded from a row
  title: string;
  note?: string;
  placeholder?: string;
  required?: boolean;
  editable?: boolean;
  rows?: number;
  onChange: (value: string) => void;
  /** Reported on every change so the form can block Save while this is invalid. */
  onValidityChange?: (valid: boolean) => void;
}

export const JsonField = ({
  value,
  title,
  note,
  placeholder,
  required = false,
  editable = true,
  rows = 10,
  onChange,
  onValidityChange,
}: JsonFieldProps) => {
  // A row loaded from the API can hand over an object; everything after the
  // first keystroke is a string.
  const initial = useMemo(() => {
    if (value === null || value === undefined) return '';
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [text, setText] = useState<string>(initial);
  const [touched, setTouched] = useState(false);

  const validation = useMemo(() => validateJson(text, { required }), [text, required]);

  // The parent passes a fresh arrow on every render, so it's held in a ref —
  // depending on it directly would re-fire the effect forever.
  const notify = useRef(onValidityChange);
  notify.current = onValidityChange;

  useEffect(() => {
    notify.current?.(validation.valid);
  }, [validation.valid]);

  // A field hidden by showWhenTrue unmounts. Without this it would leave a
  // stale "invalid" behind and Save would stay disabled with nothing to fix.
  useEffect(() => {
    return () => notify.current?.(true);
  }, []);

  const handleChange = useCallback(
    (next: string) => {
      setText(next);
      setTouched(true);
      onChange(next);
    },
    [onChange],
  );

  const handleFormat = useCallback(() => {
    if (!validation.valid || validation.parsed === null) return;
    const formatted = JSON.stringify(validation.parsed, null, 2);
    setText(formatted);
    onChange(formatted);
  }, [validation, onChange]);

  const showError = touched && !validation.valid;
  const summary = validation.valid ? describe(validation.parsed) : null;

  return (
    <FormControl sx={{ width: '100%', mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2">{`${title}${required ? ' *' : ''}`}</Typography>
        {summary && <Chip size="small" variant="outlined" label={summary} />}
        {showError && <Chip size="small" color="error" label="Invalid JSON" />}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          onClick={handleFormat}
          disabled={!editable || !validation.valid || !text.trim()}
        >
          Format
        </Button>
      </Stack>

      <Box
        sx={{
          '& textarea': {
            fontFamily: 'monospace',
            fontSize: 13,
            minHeight: `${rows * 18}px`,
            ...(showError ? { borderColor: 'error.main' } : {}),
          },
        }}
      >
        <Textarea
          placeholder={placeholder || title}
          value={text}
          onChange={(val: string) => {
            if (editable) handleChange(val);
          }}
        />
      </Box>

      {showError ? (
        <FormHelperText error>
          {validation.line
            ? `${validation.error} (line ${validation.line}, column ${validation.column})`
            : validation.error}
        </FormHelperText>
      ) : (
        note && <FormHelperText>{note}</FormHelperText>
      )}
    </FormControl>
  );
};